/**
 * useCreateEltJob Hook
 *
 * React Query mutation hook for creating an ELT job.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { eltJobService } from '../../../api/elt-job.service';
import { ELT_JOBS_QUERY_KEY } from './useEltJobs';
import type { EltJobCreateDTO, EltJob } from '../../../types/elt-job.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface UseCreateEltJobOptions {
  onSuccess?: (eltJob: EltJob) => void;
  onError?: (error: string) => void;
}

export function useCreateEltJob(tenantId: string, options?: UseCreateEltJobOptions) {
  const queryClient = useQueryClient();

  return useMutation<EltJob, Error, EltJobCreateDTO>({
    mutationFn: (data: EltJobCreateDTO) =>
      eltJobService.createEltJob(tenantId, data),
    onSuccess: (eltJob) => {
      queryClient.invalidateQueries({ queryKey: [ELT_JOBS_QUERY_KEY, tenantId] });
      options?.onSuccess?.(eltJob);
    },
    onError: (error) => {
      options?.onError?.(getErrorMessage(error));
    },
  });
}
